import {
  QueryDateValue,
  QueryParam,
  QuerySchema,
  QuerySchemaItem,
  QuerySchemaMap,
} from '../types';
import { QUERY_PARAM_TYPE, PATH_DELIMITER } from '../constants';

/**
 * Create map of complex keys, like "contact.details.email", to schema items
 * Keys are guaranteed to follow nesting order, so "contact.details.email" entry
 * will be inserted after "contact.details" and after "contact" entries
 */
export function convertSchemaTreeToMap(
  schema: QuerySchema,
  parentPath = ''
): QuerySchemaMap {
  const schemaItems = Object.values(schema);
  let result: QuerySchemaMap = {};

  for (const schemaItem of schemaItems) {
    const fullPath = parentPath
      ? parentPath + PATH_DELIMITER + schemaItem.key
      : schemaItem.key;

    result[fullPath] = schemaItem;

    if (schemaItem.properties) {
      const nested = convertSchemaTreeToMap(schemaItem.properties, fullPath);
      result = { ...result, ...nested };
    }
  }

  return result;
}

/** A string type param can be initialized automatically with simple string */
export function getDefaultSubmitKey(schema: QuerySchemaMap) {
  const schemaItemKeys = Object.keys(schema);
  const schemaItemKey = schemaItemKeys.find((key: string): boolean => {
    return schema[key].type === QUERY_PARAM_TYPE.STRING;
  });

  return schemaItemKey;
}

/** Complex query param should point to a non-empty simple value */
export function isQueryParamComplete(options: {
  param: QueryParam;
  schema: QuerySchemaMap;
}): boolean {
  const schemaItem = options.schema[options.param.key];

  if (schemaItem.type === QUERY_PARAM_TYPE.STRING) {
    return !!options.param.value;
  }

  if (schemaItem.type === QUERY_PARAM_TYPE.DATE_RANGE) {
    const paramDateValue = options.param.value as QueryDateValue;
    return !!(paramDateValue && paramDateValue.to && paramDateValue.from);
  }

  return false;
}

/** Exclude complete query params from available options, including whole subtrees */
export function excludeParamsFromSchema(options: {
  query: QueryParam[];
  schema: QuerySchemaMap;
}): QuerySchema {
  const schemaItemPaths = Object.keys(options.schema);
  const queryParamPaths = options.query.map((q: QueryParam): string => q.key);
  const schemaWithExcludedParams: QuerySchemaMap = {};

  /* Filter out schema items which are already in query */
  for (const path of schemaItemPaths) {
    if (queryParamPaths.includes(path)) {
      continue;
    }

    schemaWithExcludedParams[path] = options.schema[path];
  }

  /* Filter out object schema items with no children */
  const schemaWithExcludedParamsPaths = Object.keys(schemaWithExcludedParams);
  const schemaWithExcludedSubtrees: QuerySchemaMap = {};

  for (let i = 0; i < schemaWithExcludedParamsPaths.length; i++) {
    const path = schemaWithExcludedParamsPaths[i];
    const schemaItem = schemaWithExcludedParams[path];

    if (schemaItem.type !== QUERY_PARAM_TYPE.OBJECT) {
      schemaWithExcludedSubtrees[path] = schemaItem;
      continue;
    }

    /* Check children of object param, and check if at least one of them is a simple value */
    const otherPaths = schemaWithExcludedParamsPaths.slice(i + 1);
    for (const otherPath of otherPaths) {
      const otherSchemaItem = schemaWithExcludedParams[otherPath];

      if (otherPath.startsWith(path) && otherSchemaItem.type !== QUERY_PARAM_TYPE.OBJECT) {
        schemaWithExcludedSubtrees[path] = schemaItem;
        break;
      }
    }
  }

  return schemaWithExcludedSubtrees;
}

/**
 * For incomplete object param like "contact" find all direct children,
 * like "contact.name" and "contact.details"
 */
export function getOptionsFromSchemaForIncompleteParam(options: {
  param?: QueryParam;
  schema: QuerySchemaMap;
}): QuerySchemaItem[] {
  const paramKey = options.param ? options.param.key : '';
  const paramPathLength = paramKey.length
    ? paramKey.split(PATH_DELIMITER).length
    : 0;

  const schemaItemPaths = Object.keys(options.schema);
  const result: QuerySchemaItem[] = [];

  for (const schemaItemPath of schemaItemPaths) {
    const schemaItemPathLength = schemaItemPath.split(PATH_DELIMITER).length;
    const isNestedPath = schemaItemPath.startsWith(paramKey);
    const isDirectChild = schemaItemPathLength === paramPathLength + 1;

    if (isNestedPath && isDirectChild) {
      result.push(options.schema[schemaItemPath]);
    }
  }

  return result;
}
